import appConfig from '../../config/appConfig';
import httpErrorsTypes from '../../consts/analytics/registrationErrorsData';
import * as errorPageTypes from '../../consts/app/errorPageTypes';
import * as logMessageTypes from '../../consts/app/logMessageTypes';
import uamTimeouts from '../../consts/config/uamTimeouts';
import * as dataCollectionTypes from '../../consts/uam/dataCollectionTypes';
import * as uamMethods from '../../consts/uam/uamMethods';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapFlowTypes from '../../consts/yap/yapFlowTypes';
import * as yapRunCommandActionTypes from '../../consts/yap/yapRunCommandActionTypes';
import {setFetchTokenInProgress} from '../../state/ducks/auth';
import {
    setYapAssets,
    setYapCancelDataCollectionInProgress,
    setYapLastRequestId,
    setYapRequestActionTypeData,
    setYapUpdateSettingsInProgress,
} from '../../state/ducks/yapEncrypted';
import {makeSelectIotDeviceData} from '../../state/selectors/iotDevice';
import {
    makeSelectAssetIdByType,
    makeSelectIsYapDataCollectionAllowed,
    makeSelectIsYapSynchronizeDataCollectionInProgress,
    makeSelectIsYapUpdateSettingsInProgress,
    makeSelectYapCancelDataCollectionInProgress,
    makeSelectYapLastRequestId,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import appErrorService from '../app/appErrorService';
import backendService from '../app/backendService';
import cmService from '../communicationLayer/cmService';
import iccService from '../icc/iccService';
import log from '../logger/log';
import {uamAssetsMapping} from '../mapping/uamMappings';
import appRouterService from '../route/appRouterService';
import userBrowserService from '../user/userBrowserService';
import yapClientService from '../yap/yapClientService';
import yapConnectionService from '../yap/yapConnectionService';
import yapFlowTimeoutService from '../yap/yapFlowTimeoutService';
import yapStateService from '../yap/yapStateService';
import * as dataCollectionService from './dataCollectionService';
import UamClient from './uamClient';

const getAssets = async ({isShowError, isHolder} = {}) => {
    if (!backendService.isUamBackend()) return false;

    let isValid = false;

    try {
        isValid = await iccService.fetchUamToken({isHolder});
        dispatch(setFetchTokenInProgress(false));
        if (isValid) {
            const response = await new UamClient().callGet({
                methodName: uamMethods.GET_ASSETS,
                isHolder,
                ignoreErrorCodes: httpErrorsTypes.NOT_FOUND,
                isNeedRedirectForOtherErrors: false,
            });

            isValid = response !== null;
            if (isValid) {
                const assets = uamAssetsMapping(response, isHolder);

                dispatch(setYapAssets(assets));

                yapStateService.updateAssetMedia(assets);
            }
        }
    } catch (e) {
        isValid = false;
    }

    if (isShowError && !isValid) {
        appRouterService.forwardToErrorHandlingPage(errorPageTypes.UAM_TEMPORARY_ERROR);
    }
    return isValid;
};

const getUamInfo = async (assetId) => {
    if (!assetId) return false;

    const deviceInfos = await new UamClient().callGet({
        methodName: uamMethods.GET_DEVICE_INFO,
        requestConfig: {args: assetId},
        isNeedRedirectForOtherErrors: false,
    });

    if (!deviceInfos) return false;

    const deviceInfo = deviceInfos ? deviceInfos[0] : {};

    if (!deviceInfo) return false;

    synchronizeDataRequestCurrentTries = 0;
    return yapStateService.updateIotDevice(assetId, deviceInfo);
};

const updateUamSettings = async (body, isHolder) => {
    const isYapUpdateSettingsInProgress = makeSelectIsYapUpdateSettingsInProgress(isHolder)(getState());

    if (isYapUpdateSettingsInProgress) return;

    yapFlowTimeoutService.clearFlowTimeout(yapFlowTypes.UPDATE_SETTINGS, isHolder);

    dispatch(setYapUpdateSettingsInProgress(true, isHolder));
    dispatch(setYapRequestActionTypeData({isHolder}));

    const isCancelSuccess = await yapClientService.waitUntilDataCollectionCanceled();
    const state = getState();
    const assetId = makeSelectAssetIdByType(isHolder)(state);

    if (assetId && isCancelSuccess) {
        try {
            await yapConnectionService.syncAndSubscribeOnDevice(isHolder);
            const isConnected = await cmService.waitUntilConnected({isHolder});

            if (!isConnected) {
                appErrorService.showDeviceSettingsError(isHolder);
                dispatch(setYapUpdateSettingsInProgress(false, isHolder));
                return;
            }

            log.info('Start updateUamSettings process.');

            const {requestId} =
                (await new UamClient().callPost({
                    methodName: uamMethods.UPDATE_SETTINGS,
                    requestConfig: {args: assetId, data: body},
                    isNeedRedirectForOtherErrors: false,
                })) || {};

            if (requestId) {
                dispatch(setYapLastRequestId(requestId, isHolder));

                dispatch(
                    setYapRequestActionTypeData({
                        isHolder,
                        actionType: yapRunCommandActionTypes.UPDATE_SETTINGS,
                        newValue: body,
                    })
                );

                yapFlowTimeoutService.createFlowTimeout({
                    type: yapFlowTypes.UPDATE_SETTINGS,
                    timeoutInSec: uamTimeouts.UAM_UPDATE_SETTING_TIMEOUT,
                    isHolder,
                });
                return;
            }
        } catch (e) {
            log.error(`uamClientService: updateUamSettings failed. error: ${e}`, logMessageTypes.UAM);
        }
    }

    appErrorService.showDeviceSettingsGlobalError();
    dispatch(setYapUpdateSettingsInProgress(false, isHolder));
};

const connectDevice = (assetId) =>
    new UamClient().callGet({
        methodName: uamMethods.CONNECT_DEVICE,
        requestConfig: {args: assetId},
        isNeedRedirectForOtherErrors: false,
    });

const assetSubscribe = (assetId) =>
    new UamClient().callGet({
        methodName: uamMethods.ASSET_SUBSCRIBE,
        requestConfig: {args: assetId},
        isNeedRedirectForOtherErrors: false,
    });

const getUamDevicesExperienceData = async ({assetId, data, headerData, codentify}) => {
    if (!data || !assetId) {
        return;
    }

    const {args, params} = data || {};
    const requestConfig = {
        args: [assetId, ...args],
        params,
    };

    if (headerData) {
        const {authToken, apiKey} = headerData || {};

        requestConfig.headers = {id_token: authToken, 'X-API-Key': apiKey};
    }

    try {
        return await new UamClient().callGet({
            methodName: uamMethods.GET_DEVICES_EXPERIENCE_DATA,
            requestConfig,
            isNeedRedirectForOtherErrors: false,
            codentify,
        });
    } catch (e) {
        log.error(`getUamDevicesExperienceData  error${e}`);
    }
};

const checkUamRequestStatus = async ({assetId, requestId}) => {
    const data = await new UamClient().callGet({
        methodName: uamMethods.CHECK_REQUEST_STATUS,
        requestConfig: {args: [assetId, requestId]},
        isNeedRedirectForOtherErrors: false,
    });

    const isFailed = data?.status === yapActionStatusTypes.FAILED;
    const isSuccess = data?.status === yapActionStatusTypes.SUCCESS;

    if (isSuccess || isFailed) {
        return data;
    }

    return {status: yapActionStatusTypes.PENDING};
};

const findMyDevice = async (data) => {
    return await yapClientService.runYapCommand({
        methodName: uamMethods.FIND_MY_DEVICE,
        actionType: yapRunCommandActionTypes.FIND_DEVICE,
        params: data,
    });
};

const resetMyDevice = async (isHolder) => {
    return await yapClientService.runYapCommand({
        isHolder,
        methodName: uamMethods.RESET_DEVICE,
        actionType: yapRunCommandActionTypes.RESET_DEVICE,
    });
};

const autoClean = async () => {
    return await yapClientService.runYapCommand({
        methodName: uamMethods.AUTO_CLEAN,
        actionType: yapRunCommandActionTypes.CLEAN_DEVICE,
        customTimeout: uamTimeouts.UAM_AUTO_CLEAN_TIMEOUT,
    });
};

const SYNCHRONIZE_DATA_RETRY_COUNT = 3;
let synchronizeDataRequestCurrentTries = 0;
const proceedSynchronizeAllData = async (
    isHolder,
    isRetry = false,
    dataCollectionType = dataCollectionTypes.DATA_COLLECTION_GLOBAL
) => {
    if (isRetry) {
        synchronizeDataRequestCurrentTries++;
    }

    const isGlobalRetryExceeded = synchronizeDataRequestCurrentTries >= SYNCHRONIZE_DATA_RETRY_COUNT;
    const isDiagnosticRetryExceeded =
        synchronizeDataRequestCurrentTries >= 1 &&
        dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC;

    if (isGlobalRetryExceeded || isDiagnosticRetryExceeded) {
        // set device data collection to success to run holder data collection flow or stop holder data collection
        dataCollectionService.setDataCollectionFlowReceived({isReceived: true, isHolder, dataCollectionType});

        log.error(`uamClientService: synchronizeAllData failed.`, logMessageTypes.UAM);
        return resetSynchronizeDataCollection(dataCollectionType, isHolder);
    }

    const state = getState();
    const isDataCollectionReceived = dataCollectionService.getIsDataCollectionReceived(dataCollectionType, isHolder);

    if (isDataCollectionReceived) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    const isYapSynchronizeDataCollectionInProgress = makeSelectIsYapSynchronizeDataCollectionInProgress()(state);

    if (isYapSynchronizeDataCollectionInProgress && !isRetry) return;

    const {device} = makeSelectIotDeviceData()(state) || {};
    const {isHolderConnected} = device || {};

    if (!isHolderConnected && isHolder) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    await synchronizeAllData(isHolder, dataCollectionType);
};

const getDataCollectionUserAgent = () => {
    const appId = appConfig.getIccApiHeader_a();
    const appVersion = appConfig.getAppVersion();
    const userAgent = userBrowserService.getUserAgent();

    return `${appId}/${appVersion} ${userAgent}`;
};

const resetSynchronizeDataCollection = (dataCollectionType) => {
    synchronizeDataRequestCurrentTries = 0;

    if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC) {
        return;
    }

    dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);
};

const synchronizeAllData = async (isHolder, dataCollectionType) => {
    const state = getState();
    const assetId = makeSelectAssetIdByType(isHolder)(state);

    dataCollectionService.setDataCollectionFlowInProgress(true, dataCollectionType);

    await yapConnectionService.syncAndSubscribeOnDevice(isHolder);
    const isConnected = await cmService.waitUntilConnected({isHolder});

    if (!isConnected) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    //use getState to check last actual state
    const isYapDataCollectionAllowed = makeSelectIsYapDataCollectionAllowed()(getState());

    if (!isYapDataCollectionAllowed) return resetSynchronizeDataCollection(dataCollectionType, isHolder);

    const body = {overrideEntitiesToCollect: dataCollectionService.getEntitiesToCollect(dataCollectionType)};

    const onFail = async () => {
        await helpers.timeout(1000);
        return await proceedSynchronizeAllData(isHolder, true, dataCollectionType);
    };

    const userAgent = getDataCollectionUserAgent();
    // eslint-disable-next-line no-unused-vars
    const headers = {...(await new UamClient().getHeaders(isHolder)), 'User-Agent': userAgent};

    await new UamClient()
        .callPost({
            methodName: uamMethods.SYNCHRONIZE_ALL_DATA,
            requestConfig: {args: assetId, data: body},
            isNeedRedirectForOtherErrors: false,
        })
        .then((response) => {
            const {requestId} = response || {};

            if (requestId) {
                dispatch(setYapLastRequestId(requestId, isHolder));

                yapFlowTimeoutService.createFlowTimeout({
                    type: yapFlowTypes.DATA_COLLECTION,
                    timeoutInSec: uamTimeouts.UAM_DATA_COLLECTION_TIMEOUT,
                    isHolder,
                    data: dataCollectionType,
                });
                return;
            }

            onFail();
        });
};

const CANCEL_RETRY_COUNT = 3;
let cancelCurrentTries = 0;

const cancelSynchronizeData = async (props) => {
    const {isRetry, dataCollectionType} = props || {};
    const state = getState();
    const isYapCancelDataCollectionInProgress = makeSelectYapCancelDataCollectionInProgress()(state);

    if (isYapCancelDataCollectionInProgress && !isRetry) return;
    dispatch(setYapCancelDataCollectionInProgress(true));

    const isDataCollectionInProgress = dataCollectionService.isDataCollectionInProgress(dataCollectionType);

    if (!isDataCollectionInProgress) {
        dispatch(setYapCancelDataCollectionInProgress(false));
        return;
    }

    const afterCancel = (isResetProgress = false) => {
        const isDataCollectionInProgress = dataCollectionService.isDataCollectionInProgress(dataCollectionType);

        if (isDataCollectionInProgress && isResetProgress) {
            dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);
            dispatch(setYapLastRequestId(null, isHolder));
        }

        dispatch(setYapCancelDataCollectionInProgress(false));
        cancelCurrentTries = 0;
    };

    const onFail = async (error, isResetProgress = false) => {
        if (cancelCurrentTries < CANCEL_RETRY_COUNT) {
            cancelCurrentTries++;
            await helpers.timeout(1000);
            await cancelSynchronizeData({isRetry: true, dataCollectionType});
        } else {
            log.info(`uamClientService: cancelSynchronizeData failed. error: ${error}`, logMessageTypes.UAM);
            afterCancel(isResetProgress);
        }
    };

    const deviceRequestId = makeSelectYapLastRequestId()(state);
    const holderRequestId = makeSelectYapLastRequestId(true)(state);
    const isHolder = !!holderRequestId;
    const previousRequestId = isHolder ? holderRequestId : deviceRequestId;

    if (!previousRequestId) {
        await onFail('Request id is not defined to cancel data collection', true);
        return;
    }

    const assetId = makeSelectAssetIdByType(isHolder)(state);

    if (!assetId) {
        dispatch(setYapCancelDataCollectionInProgress(false));
        return;
    }

    try {
        const response = await new UamClient().callGet({
            methodName: uamMethods.CANCEL_REQUEST,
            requestConfig: {args: [assetId, previousRequestId]},
            isNeedRedirectForOtherErrors: false,
        });

        const {code} = response || {};
        const CANCEL_SUCCESS_CODE = 'REQUEST_CANCELED';
        const REQUEST_ALREADY_FINISHED = 'REQUEST_ALREADY_FINISHED';

        if (code === CANCEL_SUCCESS_CODE || code === REQUEST_ALREADY_FINISHED) {
            setTimeout(() => afterCancel(true), uamTimeouts.UAM_DATA_COLLECTION_AFTER_CANCEL_TIMEOUT * 1000);
            return true;
        }
        await onFail('Could not cancel synchronize all data');
    } catch (e) {
        await onFail(e);
    }
};

const getAssetsByToken = async ({uamAuthToken, uamApiKey}) => {
    if (!uamAuthToken || !uamApiKey) return null;

    return await new UamClient().callGet({
        methodName: uamMethods.GET_ASSETS,
        requestConfig: {
            headers: {id_token: uamAuthToken, 'X-API-Key': uamApiKey},
        },
        isNeedRedirectForOtherErrors: false,
    });
};

export default {
    assetSubscribe,
    autoClean,
    cancelSynchronizeData,
    checkUamRequestStatus,
    connectDevice,
    findMyDevice,
    getAssets,
    getUamInfo,
    proceedSynchronizeAllData,
    resetMyDevice,
    updateUamSettings,
    getUamDevicesExperienceData,
    getAssetsByToken,
};
